<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TwentyAPI from 'dashboard/api/integrations/twenty';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  personId: {
    type: String,
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'created']);
const { t } = useI18n();

const title = ref('');
const dueDate = ref('');
const description = ref('');
const isSubmitting = ref(false);
const errorMessage = ref('');

const handleSubmit = async () => {
  if (!title.value.trim()) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    await TwentyAPI.createTask({
      title: title.value,
      due_date: dueDate.value || null,
      description: description.value || null,
      person_id: props.personId,
      conversation_id: props.conversationId,
      contact_id: props.contactId,
    });
    emit('created');
    emit('close');
  } catch (error) {
    errorMessage.value = error.response?.data?.error || t('INTEGRATION_SETTINGS.TWENTY.CREATE_TASK_ERROR', 'Failed to create task');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
    <div class="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
      <div class="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700">
        <h3 class="text-base font-semibold text-slate-900 dark:text-white">
          {{ t('INTEGRATION_SETTINGS.TWENTY.ADD_TASK', 'Add Task in Twenty CRM') }}
        </h3>
        <button
          type="button"
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          @click="emit('close')"
        >
          ✕
        </button>
      </div>

      <form class="mt-4 space-y-4" @submit.prevent="handleSubmit">
        <div v-if="errorMessage" class="p-3 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">
          {{ errorMessage }}
        </div>

        <div>
          <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            {{ t('INTEGRATION_SETTINGS.TWENTY.TASK_TITLE', 'Title') }} *
          </label>
          <input
            v-model="title"
            type="text"
            required
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
            placeholder="Follow up with customer"
          />
        </div>

        <div>
          <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            {{ t('INTEGRATION_SETTINGS.TWENTY.DUE_DATE', 'Due Date') }}
          </label>
          <input
            v-model="dueDate"
            type="date"
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <div>
          <label class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            {{ t('INTEGRATION_SETTINGS.TWENTY.DESCRIPTION', 'Description') }}
          </label>
          <textarea
            v-model="description"
            rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-woot-500"
            placeholder="Details about the task..."
          />
        </div>

        <div class="flex items-center justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            color="slate"
            :label="t('INTEGRATION_SETTINGS.TWENTY.CANCEL', 'Cancel')"
            @click="emit('close')"
          />
          <Button
            type="submit"
            color="blue"
            :is-loading="isSubmitting"
            :label="t('INTEGRATION_SETTINGS.TWENTY.CREATE_TASK_SUBMIT', 'Create Task')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
